import React, { MouseEvent, useContext, useEffect } from 'react';
import { Message } from 'talkplus-sdk/dist/model/types';
import Icon from '@common/assets';
import Reaction from '@components/chat/chatDetail/messageList/reaction';
import { ReactionProps, ReactionStateType } from '@components/chat/hooks/useChatReactionState';
import { ChatContext, ChatContextType } from '@components/chat/ChatContext';
import MoreMenu from '@components/chat/chatDetail/messageList/reactionMenu/moreMenu';
import Colors from '@domain/constant/colors';
import { ReactionEnum, ReactionType } from '@domain/constant/chat/ReactionType';
import usePosition from '@utils/hooks/usePosition';
import useClickAway from '@utils/hooks/useClickOutside';
import classnames from 'classnames/bind';
import styles from './index.module.scss';
const cx = classnames.bind(styles);

interface ReactionBtnProps {
  type: ReactionType;
  isSelected?: boolean;
  onClick: () => void;
}

const ReactionBtn = ({ type, isSelected, onClick }: ReactionBtnProps) => {
  return (
    <button key={type} className={cx('reactionItem', { selected: isSelected }, type)} onClick={onClick}>
      <Reaction type={type} width={24} height={24} />
    </button>
  );
};

interface ReactionMenuProps {
  message: Message;
  positionType?: 'left' | 'right' | undefined;
  reactionsState: ReactionStateType;
  addReactionState: ({ reaction, callback }: { callback: () => void } & ReactionProps) => void;
  removeReactionState: ({ reaction, callback }: { callback: () => void } & ReactionProps) => void;
  isReplying?: boolean | undefined;
  handleReply: () => void;
  setIsHovered: React.Dispatch<React.SetStateAction<boolean>>;
  reactionSubmenuVisible: boolean;
  setReactionSubmenuVisible: React.Dispatch<React.SetStateAction<boolean>>;
}

// 채팅 호버 시 나타나는 리액션 리스트
const ReactionMenu = ({
  message,
  positionType = 'left',
  reactionsState,
  addReactionState,
  removeReactionState,
  isReplying,
  handleReply,
  setIsHovered,
  reactionSubmenuVisible,
  setReactionSubmenuVisible,
}: ReactionMenuProps) => {
  const { addMessageReaction, removeMessageReaction } = useContext<ChatContextType>(ChatContext);
  const { position: menuPosition, handlePosition: handleMenuPosition } = usePosition();
  const ref = React.useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = document.querySelector('#chatList');
    if (!el) return;
    el.addEventListener('scroll', handleClose);

    return () => {
      el.removeEventListener('scroll', handleClose);
    };
  }, []);

  useClickAway(() => {
    if (!ref.current) return;
    if (!reactionSubmenuVisible) return;
    handleClose();
  }, ref);

  const handleClose = () => {
    setReactionSubmenuVisible(false);
    setIsHovered(false);
  };

  const handleAddReaction = ({ reaction }: ReactionProps) => {
    addReactionState({
      reaction,
      callback: async () => {
        // 미리 선택했던 리액션에서 내가 선택한 리액션을 모두 삭제
        for await (const { isActive, type } of reactionsState) {
          isActive && (await removeMessageReaction({ message, reaction: type }));
        }

        // 내가 선택한 리액션 추가
        await addMessageReaction({ message, reaction });
      },
    });
  };

  const handleRemoveReaction = ({ reaction }: ReactionProps) => {
    removeReactionState({
      reaction,
      callback: () => removeMessageReaction({ message, reaction }),
    });
  };

  const handleMore = (event: MouseEvent<HTMLElement>) => {
    handleMenuPosition(event);
    setReactionSubmenuVisible((prev) => !prev);
  };

  const emojiEls = (Object.keys(ReactionEnum) as ReactionType[]).map((type) => {
    const isActive = reactionsState.find((reaction) => reaction.type === type)?.isActive;
    const handleClick = () => {
      isActive ? handleRemoveReaction({ reaction: type }) : handleAddReaction({ reaction: type });
    };

    return <ReactionBtn key={type} type={type} isSelected={isActive} onClick={handleClick} />;
  });

  return (
    <div ref={ref} className={cx('reactionArea', positionType)}>
      <div className={cx('reactionWrap')}>
        <div className={cx('reactionListArea')}>{emojiEls}</div>
        <hr className={cx('divider')} />
        <button className={cx('btn', 'reply', { selected: isReplying })} onClick={handleReply}>
          <Icon name={'enterLight'} width={24} height={24} fill={Colors.C_JOBDA_BLACK} />
        </button>
        <button className={cx('btn', 'more', { selected: reactionSubmenuVisible })} onClick={handleMore}>
          <Icon name={'moreHorizonLight'} width={24} height={24} fill={Colors.C_JOBDA_BLACK} />
        </button>
      </div>
      <MoreMenu message={message} visible={reactionSubmenuVisible} onClose={handleClose} position={menuPosition} />
    </div>
  );
};

export default ReactionMenu;
